import { toast } from "react-toastify";
import { useSelector } from "react-redux";
/**
 * AIGenerateOnlyModal - 素材AI画像生成モーダル（自由テキスト入力）
 *
 * フルスクリーンDialog、シンプルな1カラムレイアウト:
 * - タイトル
 * - プレビュー（生成中は進捗表示）
 * - セッション履歴（固定高さ、横スクロール）
 * - テキスト入力（10文字制限）
 * - 生成ボタン
 */
import { useState, useEffect, useRef } from "react";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { CloseBtn, FadeTransition, PlanBadge } from "common/elements";
import { GenerationStatus, AIImageResult } from "features/generation";
import { useAIMaterialImageGeneration } from "features/generation/hooks";
import {
  useSubscription,
  usePlanLimits,
  useEnforcePlanLimit,
  useOpenUpgradeOrPrompt,
} from "features/subscription";
import { CircularProgress } from "./CircularProgress";

interface AIGenerateOnlyModalProps {
  open: boolean;
  onClose: () => void;
  uid: string;
}

const MODAL_WIDTH = 900;
const MODAL_HEIGHT = 800;
const MAX_PROMPT_LENGTH = 10;
const PREVIEW_COUNT = 3; // プレビューは常に3枚固定
const HISTORY_SLOT_COUNT = 4; // 履歴は常に4スロット固定

export const AIGenerateOnlyModal = ({ open, onClose, uid }: AIGenerateOnlyModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const { plan } = useSubscription();
  const limits = usePlanLimits();
  const enforcePlanLimit = useEnforcePlanLimit();
  const openUpgradeOrPrompt = useOpenUpgradeOrPrompt();
  const aiGenerationEnabled = useSelector(
    (state: RootState) => state.config.userConfig.ai_generation,
  );

  // AI画像生成使用状況
  const usageInfo = {
    used: limits.aiImageDaily.used,
    remaining: limits.aiImageDaily.remaining,
    limit: limits.aiImageDaily.limit,
    isUnlimited: limits.aiImageDaily.unlimited,
  };

  // 1回あたりの生成枚数（プランで自動決定）
  const imageCountPerRequest = Math.max(limits.aiImagePerRequest?.limit || 1, 1);

  const [promptText1, setPromptText1] = useState("");
  const [promptText2, setPromptText2] = useState("");
  const [limitError, setLimitError] = useState<string | null>(null);
  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  // AI生成状態管理
  const {
    status: aiGenerateStatus,
    progresses: aiGenerateProgresses,
    results: sessionResults,
    generate: generateImage,
    cancel: cancelGeneration,
    reset: resetGeneration,
  } = useAIMaterialImageGeneration({ uid });

  // 生成完了の重複処理防止用ref
  const aiGenerateProcessedRef = useRef(false);

  const isGenerating = aiGenerateStatus === GenerationStatus.GENERATING;
  const isComplete = aiGenerateStatus === GenerationStatus.COMPLETE;

  // プレビュー表示用の結果（生成開始時にクリアして前回結果を履歴に移動）
  const [displayedResults, setDisplayedResults] = useState<AIImageResult[]>([]);

  // 生成開始時: プレビューをクリア（前回結果は履歴へ移動）
  // 生成完了時: 新結果をプレビューに表示
  useEffect(() => {
    if (isComplete && sessionResults.length > 0) {
      setDisplayedResults(sessionResults.slice(0, imageCountPerRequest));
    } else if (isGenerating) {
      // 生成開始時に表示をクリア（前回結果は即座に履歴へ）
      setDisplayedResults([]);
    }
  }, [isComplete, isGenerating, sessionResults, imageCountPerRequest]);

  // 履歴 = sessionResults から displayedResults を除いた部分
  const historyResults = sessionResults.filter((r) => !displayedResults.some((d) => d.id === r.id));

  // 生成完了時の処理
  useEffect(() => {
    if (aiGenerateStatus === GenerationStatus.COMPLETE && sessionResults.length > 0) {
      if (!aiGenerateProcessedRef.current) {
        aiGenerateProcessedRef.current = true;
        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_AI_IMAGE_SAVED")}</p>
          </div>,
        );
      }
    } else if (aiGenerateStatus !== GenerationStatus.COMPLETE) {
      aiGenerateProcessedRef.current = false;
    }
  }, [aiGenerateStatus, sessionResults]);

  // モーダルが開かれた時にリセット
  useEffect(() => {
    if (open) {
      resetGeneration();
      setPromptText1("");
      setPromptText2("");
      // 保護者許可チェック
      if (!aiGenerationEnabled) {
        setLimitError(I18n.t("MSG_ASSISTANT_PARENT_NOT_ALLOWED"));
      } else {
        setLimitError(null);
      }
    }
  }, [open, resetGeneration, aiGenerationEnabled]);

  const handleCancel = () => {
    if (isGenerating) {
      cancelGeneration();
    }
    play();
    btnDelay(() => {
      onClose();
    });
  };

  // TRIAL は新 Dialog、FREE は RestrictedFeatureDialog
  const handleImageLimit = () => {
    enforcePlanLimit(RestrictedFeatureSource.AI_IMAGE_LIMIT);
  };

  // PlanBadgeクリック時: TRIAL は新 Dialog、それ以外は UpgradePromotion
  const handlePlanBadgeClick = () => {
    play();
    openUpgradeOrPrompt("plan_badge");
  };

  const handlePromptChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_PROMPT_LENGTH) {
      setPromptText1(value);
    }
  };

  const handlePromptChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_PROMPT_LENGTH) {
      setPromptText2(value);
    }
  };

  // ボタン表示状態（どちらかに入力があり、生成中でない、保護者許可済み）
  const canGenerate =
    (promptText1.trim().length > 0 || promptText2.trim().length > 0) &&
    !isGenerating &&
    aiGenerationEnabled;

  const handleGenerateClick = () => {
    // 制限チェック → RestrictedFeatureDialog（プラン別対応）
    if (!usageInfo.isUnlimited && usageInfo.used >= usageInfo.limit) {
      handleImageLimit();
      return;
    }

    // 入力チェック（どちらかに入力が必要）
    const combinedPrompt = [promptText1.trim(), promptText2.trim()].filter(Boolean).join(" ");
    if (combinedPrompt.length === 0 || isGenerating) {
      return;
    }

    // 生成実行
    play();
    btnDelay(() => {
      generateImage({ prompt: combinedPrompt, imageCount: imageCountPerRequest });
    });
  };

  const handleCancelGeneration = () => {
    play();
    btnDelay(() => {
      cancelGeneration();
    });
  };

  const handleHistoryClick = (_result: AIImageResult) => {
    play();
    // 履歴クリックで何かする場合はここに実装
  };

  const scrollHistory = (direction: "left" | "right") => {
    if (historyRef.current) {
      const scrollAmount = 120; // 100px + gap
      historyRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        maxWidth={false}
        sx={{ zIndex: 999999999 }}
        TransitionComponent={FadeTransition}
        PaperProps={{ style: { backgroundColor: "transparent" } }}
      >
        <div className="flex-col-center w-full h-full bg-textcolor-black/50">
          <div
            className="flex-col-el flex-center relative"
            style={{
              width: MODAL_WIDTH * scale,
              height: MODAL_HEIGHT * scale,
            }}
          >
            <div
              className="flex-col-el border-solid border-white border-[8px] rounded-[8px] bg-beige-60 relative"
              style={{
                width: MODAL_WIDTH,
                height: MODAL_HEIGHT,
                transform: `scale(${scale})`,
              }}
            >
              <CloseBtn close={handleCancel} />

              {/* タイトル */}
              <div className="flex-row-el flex-center shrink-0 pt-4">
                <div className="flex-row-el items-center gap-2">
                  <span className="material-symbols-outlined text-brown-80 !text-[36px]">
                    auto_awesome
                  </span>
                  <p className="text-body text-[32px] text-brown-80">
                    {I18n.t("MSG_AI_MATERIAL_MODAL_TITLE")}
                  </p>
                </div>
              </div>

              {/* 制限エラー表示 */}
              {limitError && (
                <div className="flex-row-el justify-center py-3 bg-orange-100 mt-2">
                  <p className="text text-sm text-orange-700">{limitError}</p>
                </div>
              )}

              {/* 全プラン向け: PlanBadge + プラン説明（タイトルの下） */}
              <div
                className="flex-row-el flex-center shrink-0 mt-2 gap-3 cursor-pointer"
                onClick={handlePlanBadgeClick}
              >
                <p className="text-body text-[18px] text-brown-80">
                  {I18n.t("MSG_AI_IMAGE_PLAN_LABEL")}
                </p>
                <PlanBadge plan={plan} size="small" />
                {usageInfo.isUnlimited ? (
                  <p className="text-body text-[18px] text-brown-80">
                    {I18n.t("MSG_AI_IMAGE_UNLIMITED")} /{" "}
                    {I18n.t("MSG_AI_IMAGE_COUNT_PER_REQUEST", { count: imageCountPerRequest })}
                  </p>
                ) : (
                  <p
                    className={`text-body text-[18px] ${usageInfo.remaining === 0 ? "text-red-100" : "text-brown-80"}`}
                  >
                    {I18n.t("MSG_AI_IMAGE_USAGE_TODAY", {
                      remaining: usageInfo.remaining,
                      limit: usageInfo.limit,
                    })}
                  </p>
                )}
              </div>

              {/* メインコンテンツ（1カラム） */}
              <div className="flex-col-el flex-1 p-4 overflow-hidden">
                {/* プレビュー領域（常に3枚固定） */}
                <div className="flex-row-el flex-center shrink-0 mb-4 gap-4">
                  {Array.from({ length: PREVIEW_COUNT }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-col-el flex-center bg-white rounded-[8px] border-2 border-gray-20 overflow-hidden"
                      style={{ width: 200, height: 200 }}
                    >
                      {isGenerating ? (
                        // 生成中: imageCountPerRequest枚まで進捗表示、それ以外はプレースホルダー
                        i < imageCountPerRequest ? (
                          <CircularProgress progress={aiGenerateProgresses.get(i) ?? 0} size={80} />
                        ) : (
                          <span className="material-symbols-outlined text-gray-40 !text-[80px]">
                            auto_awesome
                          </span>
                        )
                      ) : displayedResults[i] ? (
                        // 生成完了: 結果画像
                        <img
                          src={displayedResults[i].url}
                          alt={`generated-${displayedResults[i].id}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        // 初期状態: プレースホルダー
                        <span className="material-symbols-outlined text-gray-40 !text-[80px]">
                          auto_awesome
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* セッション履歴（固定高さ、常に4スロット固定） */}
                <div className="flex-row-el shrink-0 h-[120px] mb-4 items-center justify-center gap-2">
                  {/* 左矢印 */}
                  <button
                    onClick={() => scrollHistory("left")}
                    className="flex-col-el flex-center shrink-0 w-[40px] h-[40px] bg-white rounded-full border-2 border-gray-20 hover:bg-gray-10 cursor-pointer"
                  >
                    <span className="material-symbols-outlined !text-[28px] text-gray-80">
                      chevron_left
                    </span>
                  </button>

                  {/* 履歴コンテナ（最小4スロット、履歴が多い場合は全て表示してスクロール可能） */}
                  <div
                    ref={historyRef}
                    className="flex-row-el gap-3 overflow-x-auto items-center"
                    style={{ maxWidth: 460, scrollbarWidth: "none" }}
                  >
                    {Array.from({
                      length: Math.max(HISTORY_SLOT_COUNT, historyResults.length),
                    }).map((_, i) => {
                      const item = historyResults[i];
                      return (
                        <div
                          key={i}
                          onClick={() => item && handleHistoryClick(item)}
                          className={`flex-col-el flex-center shrink-0 w-[100px] h-[100px] rounded-[8px] border-2 overflow-hidden ${
                            item
                              ? "bg-white border-gray-20 cursor-pointer hover:border-green-60"
                              : "bg-gray-10 border-dashed border-gray-30"
                          }`}
                        >
                          {item && (
                            <img
                              src={item.url}
                              alt={`history-${item.id}`}
                              className="w-full h-full object-contain"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* 右矢印 */}
                  <button
                    onClick={() => scrollHistory("right")}
                    className="flex-col-el flex-center shrink-0 w-[40px] h-[40px] bg-white rounded-full border-2 border-gray-20 hover:bg-gray-10 cursor-pointer"
                  >
                    <span className="material-symbols-outlined !text-[28px] text-gray-80">
                      chevron_right
                    </span>
                  </button>
                </div>

                {/* ラベル + 入力2つ + ボタン（すべて縦並び） */}
                <div className="flex-col-el shrink-0 items-center justify-center gap-4">
                  <p className="text-body text-[24px] text-brown-80 shrink-0">
                    {I18n.t("MSG_AI_MATERIAL_PROMPT_LABEL")}
                  </p>
                  <div className="flex-col-el gap-2">
                    <div className="relative w-[400px]">
                      <input
                        ref={inputRef1}
                        type="text"
                        value={promptText1}
                        onChange={handlePromptChange1}
                        placeholder={I18n.t("MSG_AI_MATERIAL_PROMPT_PLACEHOLDER_1")}
                        disabled={isGenerating || !aiGenerationEnabled}
                        className="input-modal text-body text-textcolor-black text-[28px] w-full h-[64px] pr-[70px]"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[20px] text-gray-80">
                        {promptText1.length}/{MAX_PROMPT_LENGTH}
                      </span>
                    </div>
                    <div className="relative w-[400px]">
                      <input
                        ref={inputRef2}
                        type="text"
                        value={promptText2}
                        onChange={handlePromptChange2}
                        placeholder={I18n.t("MSG_AI_MATERIAL_PROMPT_PLACEHOLDER_2")}
                        disabled={isGenerating || !aiGenerationEnabled}
                        className="input-modal text-body text-textcolor-black text-[28px] w-full h-[64px] pr-[70px]"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[20px] text-gray-80">
                        {promptText2.length}/{MAX_PROMPT_LENGTH}
                      </span>
                    </div>
                  </div>
                  <div className="alpha" style={{ width: 220 }}>
                    {isGenerating ? (
                      <button className="btn btn-danger" onClick={handleCancelGeneration}>
                        <div className="flex-row-el flex-center gap-1 mb-[3px]">
                          <span className="material-symbols-outlined !text-4xl">close</span>
                          <p className="text-body text-white text-font-size-body1">
                            {I18n.t("MSG_ICON_CHANGE_AI_CANCEL")}
                          </p>
                        </div>
                      </button>
                    ) : (
                      <button
                        className={`btn ${canGenerate ? "btn-primary" : "btn-disabled"}`}
                        onClick={handleGenerateClick}
                        disabled={!canGenerate}
                      >
                        <div className="flex-row-el flex-center gap-1 mb-[3px]">
                          <span className="material-symbols-outlined !text-4xl">auto_awesome</span>
                          <p className="text-body text-white text-font-size-body1">
                            {I18n.t("MSG_AI_MATERIAL_GENERATE_BUTTON")}
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
